<template>
  <div class="p-3 lg:p-6 bg-white selection:bg-blue-500 selection:text-black">
    <div class="flex justify-between items-center mb-4">
      <div class="flex items-center gap-2">
        <h2 class="!text-[14px] font-semibold text-slate-800 uppercase">
          Players Live
        </h2>
      </div>

      <div class="flex items-center gap-3">
        <button
          @click="openDetailedModal(null)"
          class="text-xs font-semibold text-indigo-600 hover:text-indigo-800 transition-colors uppercase"
        >
          Show All
        </button>

        <div
          class="bg-green-50 border border-green-100 px-3 py-1.5 rounded-full flex items-center gap-2"
        >
          <span class="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
          <span class="text-green-700 font-semibold text-sm">
            {{ totalPlayers }} LIVE
          </span>
        </div>
      </div>
    </div>

    <div
      v-if="!isLoading"
      class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-2 custom-overflow"
    >
      <ActivityCard
        v-for="room in limitedRooms"
        :key="room.game_id"
        :title="room.game_name"
        :value="room.value"
        :secondValue="room.skins?.length || 0"
        :image="getGameImage(room)"
        :is-fallback="room.isFallback"
        @open-modal="openDetailedModal(room)"
        @img-error="handleImageError($event, room)"
      />
    </div>

    <!-- Loading State -->
    <div
      v-else
      class="flex flex-col items-center justify-center py-10 min-h-[140px] w-full"
    >
      <Loader />
      <span class="text-xs font-semibold text-slate-400 mt-3 tracking-wide uppercase">
        Loading live players...
      </span>
    </div>

    <Teleport to="body">
      <Transition name="fade">
        <div
          v-if="showFullModal"
          @click.self="showFullModal = false"
          class="fixed top-0 left-0 w-full h-full z-[1234567890000] bg-black/60 backdrop-blur-sm flex items-start justify-center py-2 px-2 lg:p-0 lg:px-12  align-items-center selection:bg-blue-500 selection:text-black"
        >
          <div
            :class="selectedGame ? 'max-w-[1400px]' : 'max-w-[1800px]'"
            class="bg-slate-50 w-full max-h-full rounded-3xl shadow-2xl flex flex-col overflow-hidden"
          >
            <div
              class="bg-white border-b px-4 py-2 flex justify-between items-center shrink-0"
            >
              <div class="flex items-center gap-3">
                <button
                  v-if="selectedGame"
                  @click="selectedGame = null"
                  class="h-9 w-9 flex items-center justify-center hover:bg-slate-100 rounded-full transition-colors text-slate-600"
                >
                  <i class="fa-solid fa-arrow-left"></i>
                </button>
                <div>
                  <h2
                    class="text-xl font-semibold text-slate-800 leading-tight mb-0"
                  >
                    {{ selectedGame ? "Game Detail" : "Live Activity Monitor" }}
                  </h2>
                  <p class="text-sm text-slate-500 font-medium mb-0">
                    {{
                      selectedGame
                        ? selectedGame.game_name
                        : `Monitoring of ${rooms.length} games`
                    }}
                  </p>
                </div>
              </div>
              <button
                @click="closeModal"
                class="h-10 w-10 flex items-center justify-center hover:bg-slate-100 rounded-full text-slate-600"
              >
                <i class="fa-solid fa-xmark text-2xl"></i>
              </button>
            </div>

            <div v-if="!selectedGame" class="bg-white px-6 py-1 border-b">
              <div class="relative max-w-md ms-auto">
                <i
                  class="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm"
                ></i>
                <input
                  v-model="searchQuery"
                  type="text"
                  placeholder="Search games..."
                  class="w-full pl-10 pr-4 py-2 bg-white border border-1 border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 outline-none transition-all text-black placeholder:!text-black selection:bg-blue-500 selection:text-black"
                />
              </div>
            </div>

            <div class="flex-1 overflow-y-auto p-3 lg:p-8">
              <div
                v-if="selectedGame"
                class="animate-in fade-in slide-in-from-bottom-4 duration-300 space-y-3"
              >
                <div
                  class="flex flex-col md:flex-row gap-8 items-start bg-white p-3 rounded-2xl shadow-sm"
                >
                  <div class="w-full md:w-1/4">
                    <div
                      class="rounded-xl overflow-hidden border border-gray-100 shadow-sm max-w-[300px] mx-auto"
                    >
                      <img
                        :src="getGameImage(selectedGame)"
                        class="w-full object-cover max-h-[250px]"
                        @error="setFallbackImage($event, selectedGame)"
                        :class="{
                          'p-4 object-contain bg-slate-50':
                            selectedGame.isFallback,
                        }"
                      />
                    </div>

                    <div class="mt-4 flex justify-between items-center px-1">
                      <span
                        class="font-bold uppercase text-[10px] text-slate-400 tracking-wider"
                        >Total Live Players</span
                      >
                      <span
                        class="bg-emerald-500/20 text-emerald-700 text-[11px] font-bold px-3 py-1 rounded-full flex items-center gap-1.5 border border-emerald-500/30"
                      >
                        <i class="fa-solid fa-user text-[10px]"></i>
                        {{ selectedGame.value }}
                      </span>
                    </div>
                  </div>

                  <div class="flex-1 w-full">
                    <h3
                      class="text-slate-800 font-bold uppercase text-[10px] mb-4 tracking-widest flex items-center gap-2"
                    >
                      {{ selectedGame.skins?.length ?? 0 }} Active Skins
                      <div class="h-px bg-gray-100 flex-1"></div>
                    </h3>

                    <div
                      v-if="selectedGame.skins?.length > 0"
                      class="grid grid-cols-2 lg:grid-cols-4 gap-3 overflow-y-auto max-h-[220px] pr-2 custom-scrollbar"
                    >
                      <div
                        v-for="skin in selectedGame.skins"
                        :key="skin.key"
                        class="flex items-center justify-between px-4 py-2 rounded-full border transition-all"
                        :class="
                          skin.value > 0
                            ? 'bg-white border-blue-100'
                            : 'bg-slate-50 border-gray-100 opacity-60'
                        "
                      >
                        <span
                          class="text-xs font-bold text-slate-600 truncate mr-2"
                          >{{ skin.label }}</span
                        >
                        <span
                          class="text-[10px] font-black px-2 py-1 rounded-full flex items-center justify-center min-w-[24px]"
                          :class="
                            skin.value > 0
                              ? 'bg-blue-500 text-white'
                              : 'bg-slate-200 text-slate-500'
                          "
                        >
                          {{ skin.value }}
                        </span>
                      </div>
                    </div>
                    <div v-else class="text-slate-400 italic text-sm py-4">
                      No active skins detected
                    </div>
                  </div>
                </div>

                <div class="pt-2" v-if="!isLiveCasino">
                  <LiveRoundInformation :selectedGame="selectedGame" />
                </div>
              </div>

              <div v-else>
                <div
                  class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-2"
                >
                  <ActivityCard
                    v-for="game in paginatedGames"
                    :key="game.game_id"
                    :title="game.game_name"
                    :value="game.value"
                    :secondValue="game.skins?.length || 0"
                    :tags="game.skins ?? []"
                    :image="getGameImage(game)"
                    :is-fallback="game.isFallback"
                    @open-modal="openDetailedModal(game)"
                    @img-error="handleImageError($event, game)"
                  />
                </div>

                <div v-if="hasMore" class="mt-4 flex justify-center pb-0">
                  <button
                    @click="loadMore"
                    class="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full font-semibold text-sm transition-all shadow-lg hover:shadow-indigo-200"
                  >
                    Load More
                  </button>
                </div>

                <div
                  v-if="paginatedGames.length === 0"
                  class="text-center py-12"
                >
                  <p class="text-slate-400">
                    No games found matching your search.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script>
import { getFetch } from "@/api/axiosClient";
import { getActiveCategory } from "@/helpers/databaseMapping";
import ActivityCard from "../Utility/ActivityCard";
import LiveRoundInformation from "../LiveRoundInformation.vue";
import Loader from "@/components/Loader.vue";

export default {
  data() {
    return {
      isLoading: true,
      showFullModal: false,
      selectedGame: null,
      rooms: [],
      totalPlayers: 0,
      fetchInterval: null,
      fallbackImage: null,
      failedImages: new Set(),
      searchQuery: "",
      displayLimit: 10,
    };
  },
  components: {
    ActivityCard,
    LiveRoundInformation,
    Loader,
  },
  computed: {
    isLiveCasino() {
      if (this.$route?.meta?.section === "livecasino") return true;
      if (this.$route?.path?.includes("/livecasino")) return true;
      if (getActiveCategory() === "livecasino") return true;
      if (this.$store?.getters?.isLiveCasinoActive) return true;
      if (
        this.selectedGame?.game_name?.toLowerCase().includes("roulette") ||
        this.selectedGame?.game_id?.toLowerCase().includes("roulette") ||
        this.selectedGame?.category === "livecasino"
      ) {
        return true;
      }
      return false;
    },

    sortedRooms() {
      return [...this.rooms].sort((a, b) => {
        const valA = Number(a.value) || 0;
        const valB = Number(b.value) || 0;
        if (valB !== valA) {
          return valB - valA;
        }
        return (a.game_name || "").localeCompare(b.game_name || "");
      });
    },

    limitedRooms() {
      return this.sortedRooms.slice(0, 6);
    },

    filteredGames() {
      if (!this.searchQuery) return this.sortedRooms;
      const query = this.searchQuery.toLowerCase();
      return this.sortedRooms.filter((room) =>
        room.game_name.toLowerCase().includes(query),
      );
    },

    paginatedGames() {
      return this.filteredGames.slice(0, this.displayLimit);
    },

    hasMore() {
      return this.displayLimit < this.filteredGames.length;
    },
  },
  methods: {
    getGameImage(room) {
      // Bug fix: was using room.id (always undefined) instead of room.game_id
      if (this.failedImages.has(room.game_id)) return this.fallbackImage;
      return `https://storage.googleapis.com/camasy-images/${room.game_thumb}`;
    },
    setFallbackImage(event, room) {
      this.failedImages.add(room.game_id);
      event.target.src = this.fallbackImage;
      room.isFallback = true;
    },
    openDetailedModal(game = null) {
      this.selectedGame = game;
      this.showFullModal = true;
      this.displayLimit = 10;
      this.searchQuery = "";
    },
    closeModal() {
      this.showFullModal = false;
      this.selectedGame = null;
    },
    loadMore() {
      this.displayLimit += 10;
    },
    async fetchData(showLoading = true) {
      if (showLoading) this.isLoading = true;
      try {
        const response = await getFetch(`/settings/playerslivegames`, {}, true, true);
        const data = response?.data || response;

        if (data && data.skins) {
          const incoming = data.skins;

          // ── In-place update strategy ─────────────────────────────────────
          // Instead of replacing the whole array (which forces Vue to diff and
          // re-render every card), we surgically patch only the rooms whose
          // data has actually changed. This keeps the DOM stable on each poll.

          const existingMap = new Map(this.rooms.map((r) => [r.game_id, r]));
          const nextIds = new Set(incoming.map((r) => r.game_id));

          // Remove rooms that no longer exist.
          this.rooms = this.rooms.filter((r) => nextIds.has(r.game_id));

          for (const newRoom of incoming) {
            const existing = existingMap.get(newRoom.game_id);
            if (existing) {
              // Only update fields that can change, to preserve object identity
              // for unchanged cards (avoids unnecessary child re-renders).
              if (existing.value !== newRoom.value) existing.value = newRoom.value;
              if (JSON.stringify(existing.skins) !== JSON.stringify(newRoom.skins)) {
                existing.skins = newRoom.skins;
              }
              existing.isFallback = this.failedImages.has(newRoom.game_id);
            } else {
              // New room — append it.
              this.rooms.push({
                ...newRoom,
                isFallback: this.failedImages.has(newRoom.game_id),
              });
            }
          }

          this.totalPlayers = data.totalPlayers || 0;

          // Keep selectedGame reference in sync.
          if (this.selectedGame) {
            const updated = existingMap.get(this.selectedGame.game_id);
            if (updated) this.selectedGame = updated;
          }
        }
      } catch (error) {
        console.error("Error fetching live rooms:", error);
      } finally {
        this.isLoading = false;
      }
    },
  },
  async mounted() {
    const providerSlug = process.env.VUE_APP_PROVIDER ?? null;
    this.fallbackImage = `https://storage.googleapis.com/camasy-images/${providerSlug}/logo_promotool.png`;

    await this.fetchData();
    this.fetchInterval = setInterval(() => this.fetchData(false), 30000);
  },
  unmounted() {
    if (this.fetchInterval) clearInterval(this.fetchInterval);
  },
};
</script>

<style scoped lang="scss">
@media (max-width: 639px) {
  .custom-overflow {
    overflow-y: auto;
    max-height: 250px;
  }
}
</style>
